import {
  TransformData,
  type FlowNodeEntity,
  type FreeLayoutPluginContext,
} from '@flowgram.ai/free-layout-editor';
import {
  getMediaSafeAreaSize,
  type MediaSafeAreaResource,
} from '../hooks/useMediaSafeArea';

const RIGHT_GRID_GAP_X = 64;
const RIGHT_GRID_MIN_NODE_SPACE_X = 360;
const RIGHT_GRID_GAP_Y = 48;
const RIGHT_GRID_COLLISION_PADDING = 16;
const RIGHT_GRID_MAX_COLUMNS = 200;
const RIGHT_GRID_MAX_LANES = 20;
const RIGHT_ADJACENT_MAX_COLUMNS = 8;
const RIGHT_ADJACENT_MAX_LANES = 6;
const RIGHT_GRID_VIEWPORT_PADDING_Y = 80;
const STACKED_IMAGE_VISUAL_OVERFLOW_X = 96;
/** 图片、视频等媒体节点标题栏在画布碰撞尺寸中的固定高度。 */
export const MEDIA_NODE_HEADER_HEIGHT = 40;
const MEDIA_NODE_FALLBACK_CONTENT_WIDTH = 360;
const MEDIA_NODE_FALLBACK_CONTENT_HEIGHT = 280;
const TRANSFORM_SIZE_OUTLIER_RATIO = 1.8;
const RIGHT_ADJACENT_MAX_SOURCE_WIDTH = MEDIA_NODE_FALLBACK_CONTENT_WIDTH + STACKED_IMAGE_VISUAL_OVERFLOW_X;
const STORYBOARD_GROUP_HEADER_OVERFLOW_Y = 40;

export interface CanvasNodeBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CanvasNodeSize {
  width: number;
  height: number;
}

interface CanvasVerticalBounds {
  top: number;
  bottom: number;
}

interface RightGridPositionOptions {
  ignoreCandidateSizeForGap?: boolean;
  /** 覆盖水平列间距，供连接端口外溢较大的节点预留可视空间。 */
  gapX?: number;
}

/**
 * 多图折叠态会在主图右侧露出堆叠卡片，FlowGram bounds 不一定包含这块视觉占位。
 */
function getNodeVisualOverflowX(node: FlowNodeEntity) {
  const data = node.toJSON().data as { media_list?: unknown[]; } | undefined;
  const mediaList = data?.media_list;
  if (Array.isArray(mediaList) && mediaList.length > 1) {
    return STACKED_IMAGE_VISUAL_OVERFLOW_X;
  }
  return 0;
}

/**
 * 分镜组标题通过 transform 悬浮在容器上方，碰撞检测需要补上这段视觉外溢。
 */
function getNodeVisualOverflowTop(node: FlowNodeEntity) {
  const data = node.toJSON().data as { groupMode?: string; } | undefined;
  return data?.groupMode === 'storyboard' ? STORYBOARD_GROUP_HEADER_OVERFLOW_Y : 0;
}

/**
 * 将 FlowGram 的 bounds 统一成画布坐标矩形，兼容 Rectangle 和 left/top/right/bottom 两种结构。
 */
function normalizeBounds(bounds: Partial<CanvasNodeBounds & {
  left: number;
  top: number;
  right: number;
  bottom: number;
}>, fallbackSize?: CanvasNodeSize): CanvasNodeBounds | undefined {
  const x = typeof bounds.x === 'number' ? bounds.x : bounds.left;
  const y = typeof bounds.y === 'number' ? bounds.y : bounds.top;
  let width = fallbackSize?.width;
  let height = fallbackSize?.height;

  if (typeof bounds.width === 'number') {
    width = bounds.width;
  } else if (typeof bounds.right === 'number' && typeof bounds.left === 'number') {
    width = bounds.right - bounds.left;
  }

  if (typeof bounds.height === 'number') {
    height = bounds.height;
  } else if (typeof bounds.bottom === 'number' && typeof bounds.top === 'number') {
    height = bounds.bottom - bounds.top;
  }

  if (
    typeof x !== 'number'
    || typeof y !== 'number'
    || typeof width !== 'number'
    || typeof height !== 'number'
  ) {
    return undefined;
  }

  if (width <= 0 || height <= 0) {
    return undefined;
  }

  return {
    x,
    y,
    width,
    height,
  };
}

/**
 * 将媒体原始宽高转换为节点实际展示量级，防止高清原图像素把画布落点推得过远。
 */
function getMediaFallbackSize(
  mediaSize: Partial<CanvasNodeSize>,
): CanvasNodeSize | undefined {
  if (
    typeof mediaSize.width !== 'number'
    || typeof mediaSize.height !== 'number'
    || mediaSize.width <= 0
    || mediaSize.height <= 0
  ) {
    return undefined;
  }

  const scale = Math.min(
    MEDIA_NODE_FALLBACK_CONTENT_WIDTH / mediaSize.width,
    MEDIA_NODE_FALLBACK_CONTENT_HEIGHT / mediaSize.height,
    1,
  );

  return {
    width: MEDIA_NODE_FALLBACK_CONTENT_WIDTH,
    height: Math.max(1, Math.round(mediaSize.height * scale)) + MEDIA_NODE_HEADER_HEIGHT,
  };
}

/**
 * 用数据推断尺寸修正临时偏小或偏大的 Transform bounds，避免工具栏/加载态影响落位避让。
 */
function isBoundsSizeUnstable(
  boundsSize: CanvasNodeSize,
  fallbackSize?: CanvasNodeSize,
): boolean {
  if (!fallbackSize) return false;

  return boundsSize.width < fallbackSize.width
    || boundsSize.width > fallbackSize.width * TRANSFORM_SIZE_OUTLIER_RATIO
    || boundsSize.height < fallbackSize.height
    || boundsSize.height > fallbackSize.height * TRANSFORM_SIZE_OUTLIER_RATIO;
}

/**
 * 用数据推断尺寸修正临时偏小或偏大的 Transform bounds，避免工具栏/加载态影响落位避让。
 */
function getStableBoundsSize(
  boundsSize: CanvasNodeSize,
  fallbackSize?: CanvasNodeSize,
): CanvasNodeSize {
  if (!fallbackSize) return boundsSize;

  const shouldUseFallbackWidth = boundsSize.width < fallbackSize.width
    || boundsSize.width > fallbackSize.width * TRANSFORM_SIZE_OUTLIER_RATIO;
  const shouldUseFallbackHeight = boundsSize.height < fallbackSize.height
    || boundsSize.height > fallbackSize.height * TRANSFORM_SIZE_OUTLIER_RATIO;

  return {
    width: shouldUseFallbackWidth ? fallbackSize.width : boundsSize.width,
    height: shouldUseFallbackHeight ? fallbackSize.height : boundsSize.height,
  };
}

/**
 * 从节点数据推断一个稳定的 fallback 尺寸，避免 TransformData 尚未同步时用目标候选尺寸污染源节点位置。
 */
function getNodeDataFallbackSize(node: FlowNodeEntity): CanvasNodeSize | undefined {
  const data = node.toJSON().data as {
    media_list?: Array<Partial<CanvasNodeSize>>;
    customerFileInfo?: Partial<CanvasNodeSize>;
  } | undefined;

  const mediaSize = data?.media_list?.find((item) => (
    typeof item.width === 'number'
    && typeof item.height === 'number'
    && item.width > 0
    && item.height > 0
  ));
  const mediaFallbackSize = mediaSize ? getMediaFallbackSize(mediaSize) : undefined;
  if (mediaFallbackSize) return mediaFallbackSize;

  const customerFileInfo = data?.customerFileInfo;
  const customerFileFallbackSize = customerFileInfo ? getMediaFallbackSize(customerFileInfo) : undefined;
  if (customerFileFallbackSize) return customerFileFallbackSize;

  return undefined;
}

/**
 * 按图片节点自身展示规则推导可视尺寸，避免用异常 bounds 计算右侧邻近落点。
 */
function getNodeVisibleRenderSize(node: FlowNodeEntity): CanvasNodeSize | undefined {
  const data = node.toJSON().data as {
    renderSize?: Partial<CanvasNodeSize>;
    media_list?: Array<MediaSafeAreaResource>;
  } | undefined;

  const renderSize = data?.renderSize;
  if (
    typeof renderSize?.width === 'number'
    && typeof renderSize?.height === 'number'
    && renderSize.width > 0
    && renderSize.height > 0
  ) {
    return {
      width: renderSize.width,
      height: renderSize.height,
    };
  }

  const media = data?.media_list?.[0];
  if (
    typeof media?.width !== 'number'
    || typeof media.height !== 'number'
    || media.width <= 0
    || media.height <= 0
  ) {
    return undefined;
  }

  const mediaSize = getMediaSafeAreaSize({ media });
  return {
    width: mediaSize.width,
    height: mediaSize.height + MEDIA_NODE_HEADER_HEIGHT,
  };
}

/**
 * 读取节点当前画布矩形；布局数据不可用时回退到节点持久化坐标和传入尺寸。
 */
export function getNodeCanvasBounds(
  node: FlowNodeEntity,
  fallbackSize?: CanvasNodeSize,
): CanvasNodeBounds | undefined {
  const visualOverflowX = getNodeVisualOverflowX(node);
  const visualOverflowTop = getNodeVisualOverflowTop(node);
  const transform = node.getData<TransformData>(TransformData);
  const transformBounds = transform?.bounds;
  const normalizedTransformBounds = transformBounds
    ? normalizeBounds(transformBounds, fallbackSize)
    : undefined;
  if (normalizedTransformBounds) {
    const transformPosition = transform?.position;
    const usePositionBounds = Boolean(
      transformPosition
      && isBoundsSizeUnstable(normalizedTransformBounds, fallbackSize),
    );
    const stableSize = getStableBoundsSize(normalizedTransformBounds, fallbackSize);
    const stableX = usePositionBounds && transformPosition
      ? transformPosition.x - stableSize.width / 2
      : normalizedTransformBounds.x;
    const stableY = usePositionBounds && transformPosition
      ? transformPosition.y
      : normalizedTransformBounds.y;
    return {
      x: stableX,
      y: stableY - visualOverflowTop,
      width: stableSize.width + visualOverflowX,
      height: stableSize.height + visualOverflowTop,
    };
  }

  const position = (node.toJSON().meta as { position?: { x: number; y: number; }; } | undefined)?.position;
  if (!position || !fallbackSize) return undefined;

  return {
    x: position.x - fallbackSize.width / 2,
    y: position.y - visualOverflowTop,
    width: fallbackSize.width + visualOverflowX,
    height: fallbackSize.height + visualOverflowTop,
  };
}

/**
 * 判断两个画布矩形是否重叠，padding 用来给节点之间留出视觉间距。
 */
function isBoundsOverlapping(
  a: CanvasNodeBounds,
  b: CanvasNodeBounds,
  padding = 0,
) {
  return a.x - padding < b.x + b.width
    && a.x + a.width + padding > b.x
    && a.y - padding < b.y + b.height
    && a.y + a.height + padding > b.y;
}

/**
 * 从已有节点矩形中找出第一个与候选落点重叠的占位。
 */
function findOverlappingBounds(
  boundsList: CanvasNodeBounds[],
  candidate: CanvasNodeBounds,
  padding = 0,
) {
  return boundsList.find((bounds) => isBoundsOverlapping(candidate, bounds, padding));
}

/**
 * 生成固定数量的上下 lane 顺序：同行优先，其后上、下交替扩展。
 */
function getFallbackRightGridLaneIndexes(maxLanes = RIGHT_GRID_MAX_LANES) {
  return Array.from({ length: maxLanes * 2 + 1 }, (_, index) => {
    if (index === 0) return 0;
    const distance = Math.ceil(index / 2);
    return index % 2 === 1 ? -distance : distance;
  });
}

/**
 * 生成当前视口内可用的 Y lane 顺序；同一 X 列会先排满视口内 Y，再进入下一列。
 * lane 0 始终优先，确保第一次后链路节点与源节点同 Y，对用户形成稳定锚点。
 */
function getRightGridLaneIndexes(
  originY: number,
  rowHeight: number,
  size: CanvasNodeSize,
  visibleBounds?: CanvasVerticalBounds,
) {
  if (!visibleBounds || visibleBounds.bottom - visibleBounds.top < size.height) {
    return getFallbackRightGridLaneIndexes();
  }

  const minLaneIndex = Math.ceil((visibleBounds.top - originY) / rowHeight);
  const maxLaneIndex = Math.floor((visibleBounds.bottom - size.height - originY) / rowHeight);

  if (minLaneIndex > maxLaneIndex) {
    return getFallbackRightGridLaneIndexes();
  }

  const laneIndexes = Array.from(
    { length: maxLaneIndex - minLaneIndex + 1 },
    (_, index) => minLaneIndex + index,
  );
  const visibleLaneIndexes = laneIndexes.includes(0) ? laneIndexes : [0, ...laneIndexes];

  return visibleLaneIndexes.sort((a, b) => {
    const absDiff = Math.abs(a) - Math.abs(b);
    if (absDiff !== 0) return absDiff;
    return a - b;
  });
}

/**
 * 读取当前画布可见区域的垂直画布坐标；只用于约束上下 lane，不约束向右追加。
 */
function getVisibleCanvasVerticalBounds(ctx: FreeLayoutPluginContext): CanvasVerticalBounds | undefined {
  if (typeof document === 'undefined') return undefined;

  const playgroundEl = document.querySelector<HTMLElement>('.gedit-playground')
    || document.querySelector<HTMLElement>('.gedit-playground-layer');
  const rect = playgroundEl?.getBoundingClientRect();
  if (!rect || rect.height <= 0) return undefined;

  const top = ctx.playground.config.getPosFromMouseEvent({
    clientX: rect.left,
    clientY: rect.top + RIGHT_GRID_VIEWPORT_PADDING_Y,
  });
  const bottom = ctx.playground.config.getPosFromMouseEvent({
    clientX: rect.left,
    clientY: rect.bottom - RIGHT_GRID_VIEWPORT_PADDING_Y,
  });

  return {
    top: Math.min(top.y, bottom.y),
    bottom: Math.max(top.y, bottom.y),
  };
}

/**
 * 判断候选节点是否完整落在当前可见画布的上下边界内。
 */
function isCandidateInsideVerticalBounds(
  candidate: CanvasNodeBounds,
  visibleBounds?: CanvasVerticalBounds,
) {
  if (!visibleBounds) return true;
  if (visibleBounds.bottom - visibleBounds.top < candidate.height) return true;
  return candidate.y >= visibleBounds.top
    && candidate.y + candidate.height <= visibleBounds.bottom;
}

/**
 * 判断候选节点是否和已有节点保持避让距离。
 */
function isCandidateFree(
  allBounds: CanvasNodeBounds[],
  candidate: CanvasNodeBounds,
) {
  return !findOverlappingBounds(
    allBounds,
    candidate,
    RIGHT_GRID_COLLISION_PADDING,
  );
}

/**
 * 按列优先寻找空位：每个 X 列都从 Y=0 开始，当前列所有 Y lane 不可用后再进入下一列。
 * 返回值是用于碰撞检测的左上角 bounds；调用方负责换算成节点创建需要的 topCenter。
 */
function getFirstFreeRightGridPosition(
  allBounds: CanvasNodeBounds[],
  originX: number,
  originY: number,
  size: CanvasNodeSize,
  columnWidth: number,
  rowHeight: number,
  visibleBounds?: CanvasVerticalBounds,
) {
  const laneIndexes = getRightGridLaneIndexes(originY, rowHeight, size, visibleBounds);

  for (let columnIndex = 0; columnIndex < RIGHT_GRID_MAX_COLUMNS; columnIndex += 1) {
    const x = originX + columnIndex * columnWidth;
    for (const laneIndex of laneIndexes) {
      const candidate = {
        x,
        y: originY + laneIndex * rowHeight,
        width: size.width,
        height: size.height,
      };

      if (
        (laneIndex === 0 || isCandidateInsideVerticalBounds(candidate, visibleBounds))
        && isCandidateFree(allBounds, candidate)
      ) {
        return candidate;
      }
    }
  }

  return {
    x: originX + RIGHT_GRID_MAX_COLUMNS * columnWidth,
    y: originY,
    width: size.width,
    height: size.height,
  };
}

/**
 * 计算源节点右侧的网格化落点：同一 X 列优先在视口内查找 Y lane，找不到再增加 X 列。
 */
export function getRightGridNodePosition(
  ctx: FreeLayoutPluginContext,
  sourceNode: FlowNodeEntity,
  candidateSize?: CanvasNodeSize,
  options: RightGridPositionOptions = {},
): { x: number; y: number; } {
  const sourceBounds = getNodeCanvasBounds(sourceNode, getNodeDataFallbackSize(sourceNode));
  if (!sourceBounds) {
    return ctx.document.getNodeDefaultPosition(sourceNode.flowNodeType);
  }

  const size = candidateSize ?? {
    width: sourceBounds.width,
    height: sourceBounds.height,
  };
  const gapX = Math.max(0, options.gapX ?? RIGHT_GRID_GAP_X);
  const sourceCenterX = sourceBounds.x + sourceBounds.width / 2;
  const firstColumnCenterGap = Math.max(
    RIGHT_GRID_MIN_NODE_SPACE_X,
    (sourceBounds.width + (options.ignoreCandidateSizeForGap ? 0 : size.width)) / 2 + gapX,
  );
  const originX = sourceCenterX + firstColumnCenterGap - size.width / 2;
  const columnWidth = Math.max(size.width + gapX, RIGHT_GRID_MIN_NODE_SPACE_X);
  const laneY = sourceBounds.y;
  const rowHeight = size.height + RIGHT_GRID_GAP_Y;
  const visibleBounds = getVisibleCanvasVerticalBounds(ctx);
  const allBounds = ctx.document.getAllNodes()
    .map((node) => getNodeCanvasBounds(node, getNodeDataFallbackSize(node) ?? size))
    .filter((bounds): bounds is CanvasNodeBounds => Boolean(bounds));

  const candidate = getFirstFreeRightGridPosition(
    allBounds,
    originX,
    laneY,
    size,
    columnWidth,
    rowHeight,
    visibleBounds,
  );

  return {
    x: candidate.x + size.width / 2,
    y: candidate.y,
  };
}

/**
 * 计算源节点左侧的网格化落点：与 getRightGridNodePosition 对称，X 列向左扩展。
 * 用于输入源类节点（如导演台的全景图源）落在目标节点左侧且不与已有节点重叠。
 */
export function getLeftGridNodePosition(
  ctx: FreeLayoutPluginContext,
  sourceNode: FlowNodeEntity,
  candidateSize?: CanvasNodeSize,
  options: RightGridPositionOptions = {},
): { x: number; y: number; } {
  const sourceBounds = getNodeCanvasBounds(sourceNode, getNodeDataFallbackSize(sourceNode));
  if (!sourceBounds) {
    return ctx.document.getNodeDefaultPosition(sourceNode.flowNodeType);
  }

  const size = candidateSize ?? {
    width: sourceBounds.width,
    height: sourceBounds.height,
  };
  const gapX = Math.max(0, options.gapX ?? RIGHT_GRID_GAP_X);
  const sourceCenterX = sourceBounds.x + sourceBounds.width / 2;
  const firstColumnCenterGap = Math.max(
    RIGHT_GRID_MIN_NODE_SPACE_X,
    (sourceBounds.width + (options.ignoreCandidateSizeForGap ? 0 : size.width)) / 2 + gapX,
  );
  const originX = sourceCenterX - firstColumnCenterGap - size.width / 2;
  const columnWidth = Math.max(size.width + gapX, RIGHT_GRID_MIN_NODE_SPACE_X);
  const laneY = sourceBounds.y;
  const rowHeight = size.height + RIGHT_GRID_GAP_Y;
  const visibleBounds = getVisibleCanvasVerticalBounds(ctx);
  const allBounds = ctx.document.getAllNodes()
    .map((node) => getNodeCanvasBounds(node, getNodeDataFallbackSize(node) ?? size))
    .filter((bounds): bounds is CanvasNodeBounds => Boolean(bounds));

  // 复用列 × lane 空位搜索，columnWidth 取负使 X 列向左扩展。
  const candidate = getFirstFreeRightGridPosition(
    allBounds,
    originX,
    laneY,
    size,
    -columnWidth,
    rowHeight,
    visibleBounds,
  );

  return {
    x: candidate.x + size.width / 2,
    y: candidate.y,
  };
}

/**
 * 计算源节点右侧的邻近落点；用于需要紧贴当前操作节点展示的局部产物。
 */
export function getRightAdjacentNodePosition(
  ctx: FreeLayoutPluginContext,
  sourceNode: FlowNodeEntity,
  candidateSize?: CanvasNodeSize,
  options: RightGridPositionOptions = {},
): { x: number; y: number; } {
  const sourceBounds = getNodeCanvasBounds(sourceNode, getNodeDataFallbackSize(sourceNode));
  if (!sourceBounds) {
    return ctx.document.getNodeDefaultPosition(sourceNode.flowNodeType);
  }

  const transform = sourceNode.getData<TransformData>(TransformData);
  const sourcePosition = transform?.position ?? { x: sourceBounds.x, y: sourceBounds.y };
  const visibleSize = getNodeVisibleRenderSize(sourceNode);
  const sourceVisualWidth = Math.min(
    visibleSize?.width ?? sourceBounds.width,
    RIGHT_ADJACENT_MAX_SOURCE_WIDTH,
  ) + getNodeVisualOverflowX(sourceNode);
  const size = candidateSize ?? {
    width: sourceVisualWidth,
    height: sourceBounds.height,
  };
  const gapX = Math.max(0, options.gapX ?? RIGHT_GRID_GAP_X);
  const originX = sourcePosition.x + sourceVisualWidth + gapX;
  const originY = sourcePosition.y;
  const columnWidth = size.width + gapX;
  const rowHeight = size.height + STORYBOARD_GROUP_HEADER_OVERFLOW_Y + RIGHT_GRID_GAP_Y;
  const allBounds = ctx.document.getAllNodes()
    .filter((node) => node.id !== sourceNode.id)
    .map((node) => getNodeCanvasBounds(node, getNodeDataFallbackSize(node) ?? size))
    .filter((bounds): bounds is CanvasNodeBounds => Boolean(bounds));
  const laneIndexes = getFallbackRightGridLaneIndexes(RIGHT_ADJACENT_MAX_LANES);

  for (let columnIndex = 0; columnIndex < RIGHT_ADJACENT_MAX_COLUMNS; columnIndex += 1) {
    const x = originX + columnIndex * columnWidth;
    for (const laneIndex of laneIndexes) {
      const candidate = {
        x,
        y: originY + laneIndex * rowHeight - STORYBOARD_GROUP_HEADER_OVERFLOW_Y,
        width: size.width,
        height: size.height + STORYBOARD_GROUP_HEADER_OVERFLOW_Y,
      };

      if (isCandidateFree(allBounds, candidate)) {
        return {
          x,
          y: originY + laneIndex * rowHeight,
        };
      }
    }
  }

  return {
    x: originX,
    y: originY + rowHeight,
  };
}
